import { useCheckEvaluationMutation } from '@/services/checklists';
import { PalmettoButton, PalmettoIcon, PalmettoTag, PalmettoTextInput } from '@palmetto/dsm-components';
import React, { useCallback, useState } from 'react';

import type { EvaluationState, EvaluationView } from 'types';

import { useChecklistContext } from '../ChecklistContext';
import type { EvaluationInputComponentProps } from '../evaluationClientRegistry';

// The Annex II reference is redacted server-side before the view crosses the wire (see
// serialValidationServer.redactDerivedForView), so entered/derived here are read via a narrow
// cast rather than the shared numeric EvaluationRowView shape.
const enteredSerialFromView = (view: EvaluationView | undefined): string => {
  const entered = view?.rows[0]?.entered as unknown as { serial?: string | null } | undefined;
  return entered?.serial ?? '';
};

const panelModelFromView = (view: EvaluationView | undefined): string | undefined => {
  const derived = view?.rows[0]?.derived as unknown as { panelModel?: string } | undefined;
  return derived?.panelModel;
};

// Signature of the view's persisted entered value, used below as a local-state reset key so
// local edits re-seed only when the persisted view actually changes.
const enteredSignature = (view: EvaluationView | undefined): string => JSON.stringify(enteredSerialFromView(view));

/**
 * Unlike the tolerance evaluations, this component does NOT recompute a live verdict locally —
 * the Annex II reference never reaches the client, so there's nothing to match the entered
 * serial against here. Typing a serial always emits `incomplete`. A real verdict only comes from
 * clicking "Check", which calls a read-only server endpoint (POST .../check) that runs the exact
 * same compute logic the save path uses, without persisting anything.
 */
export const SerialValidationEvaluationInput: React.FC<EvaluationInputComponentProps> = ({
  label,
  view,
  readOnly,
  onChange,
  itemKey,
  inputKey,
}) => {
  const { checklistId } = useChecklistContext();
  const [serial, setSerial] = useState<string>(() => enteredSerialFromView(view));
  const [seededSignature, setSeededSignature] = useState<string>(() => enteredSignature(view));
  const [checkError, setCheckError] = useState<string | undefined>(undefined);
  const currentSignature = enteredSignature(view);
  if (currentSignature !== seededSignature) {
    setSerial(enteredSerialFromView(view));
    setSeededSignature(currentSignature);
    setCheckError(undefined);
  }

  const [checkEvaluation, { isLoading: checking }] = useCheckEvaluationMutation();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!view) return;
      const nextSerial = e.target.value;
      setSerial(nextSerial);
      setCheckError(undefined);
      const state: EvaluationState = {
        key: view.key,
        rows: [
          {
            entered: { serial: nextSerial } as unknown as EvaluationState['rows'][number]['entered'],
            verdict: 'incomplete',
          },
        ],
        verdict: 'incomplete',
      };
      onChange(state);
    },
    [view, onChange],
  );

  const handleCheck = useCallback(async () => {
    if (!view || !checklistId || !itemKey) return;
    try {
      const result = await checkEvaluation({ checklistId, itemKey, inputKey, entered: { serial } }).unwrap();
      setCheckError(undefined);
      onChange(result);
    } catch {
      setCheckError('Unable to check this serial right now. Try again.');
    }
  }, [view, checkEvaluation, checklistId, itemKey, inputKey, serial, onChange]);

  if (!view) return null;

  const row = view.rows[0];
  const verdict = row?.verdict;
  const panelModel = panelModelFromView(view);
  const chipColor = verdict === 'pass' ? 'success' : verdict === 'fail' ? 'error' : 'grey';
  const chipLabel = verdict === 'pass' ? 'Compliant' : verdict === 'fail' ? 'Rejected' : 'Incomplete';

  return (
    <div className="flex flex-col gap-8 py-4">
      <span className="body-bold-300 text-text-primary">{label}</span>

      {panelModel !== undefined && (
        <div className="flex flex-col gap-2">
          <span className="body-200 text-text-secondary">Panel Model</span>
          <span className="body-300 text-text-primary">{panelModel}</span>
        </div>
      )}

      <PalmettoTextInput
        id="evaluation-serialValidation-serial"
        label="Serial Number"
        value={serial}
        onChange={handleChange}
        disabled={readOnly}
      />

      {!readOnly && (
        <PalmettoButton
          type="button"
          onClick={handleCheck}
          disabled={checking || !serial || !checklistId || !itemKey || verdict !== 'incomplete'}
        >
          {checking ? 'Checking…' : 'Check'}
        </PalmettoButton>
      )}

      {checkError && <span className="body-200 text-text-error">{checkError}</span>}

      {verdict !== 'incomplete' && (
        <div className="flex items-center gap-8">
          <PalmettoTag label={chipLabel} color={chipColor} size="sm" />
          {verdict === 'pass' && <PalmettoIcon name="check" size="sm" className="text-text-success" />}
          {verdict === 'fail' && <PalmettoIcon name="x" size="sm" className="text-text-error" />}
        </div>
      )}
    </div>
  );
};
